/**
 * Distribution des valeurs de luminosité choisies.
 *
 * Une barre par valeur (1 à 100) plutôt que des tranches de dix : la
 * distribution réelle est faite de pics nets — les extrêmes 1 et 100, puis
 * quelques valeurs rondes. Les regrouper en tranches lisserait exactement ce
 * qu'il y a à voir.
 *
 * Chaque barre est un ÉVÉNEMENT de changement, pas un utilisateur : quelqu'un
 * qui tâtonne dix fois pèse dix fois. C'est voulu — on mesure le réglage
 * cherché, pas le réglage final.
 */

import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger } from '../lib/format';
import { AXIS_STYLE, COLORS } from '../lib/theme';
import type { BrightnessBucket, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<BrightnessBucket>> = [
  { key: 'value', header: 'Luminosité', numeric: true, render: (row) => String(row.value) },
  { key: 'changes', header: 'Changements', numeric: true, render: (row) => formatInteger(row.changes) },
];

interface Props {
  data: readonly BrightnessBucket[];
  median: number | null;
}

export function BrightnessChart({ data, median }: Props) {
  return (
    <ChartCard
      title="Luminosité choisie"
      subtitle="Un point par changement de réglage, de 1 à 100"
      rows={data}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
        {/* Marge haute genereuse : l'etiquette de la ligne de repere est posee
            au-dessus de l'aire de trace et serait rognee sans elle. */}
        <BarChart data={data as BrightnessBucket[]} margin={{ top: 22, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="value"
            type="number"
            domain={[0, 100]}
            ticks={[1, 25, 50, 75, 100]}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={44}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={
              <ChartTooltip
                labelFormatter={(label) => `Luminosité ${label}`}
                valueFormatter={formatInteger}
              />
            }
          />
          {median !== null && (
            // Reperer la mediane evite de lire la distribution a l'oeil : avec
            // deux pics aux extremes, l'intuition se trompe systematiquement.
            <ReferenceLine
              x={median}
              stroke={COLORS.brandCyan}
              strokeWidth={1}
              label={{
                value: `médiane ${median}`,
                position: 'top',
                fill: COLORS.textSecondary,
                fontSize: 11,
              }}
            />
          )}
          <Bar
            isAnimationActive={false}
            dataKey="changes"
            name="Changements"
            fill={COLORS.series1}
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
